import React, { useState, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import { Edit, ArrowUp, ArrowDown, Plus, Trash2, Save, Upload, X } from "lucide-react";
import adminApi from "../adminApi";
import { backendUrl } from "../../backendUrl";
import "./LockProperties.css";

interface LockProperty {
  id: number;
  name: string;
  column_name?: string;
  description?: string;
  icon_path: string | null;
  display_order: number;
  is_active: number | boolean;
  created_at?: string;
}

interface IconFile {
  name: string;
  path: string;
}

interface ColumnOption {
  name: string;
  displayName: string;
}

function LockProperties() {
  const { t } = useLanguage();
  const [properties, setProperties] = useState<LockProperty[]>([]);
  const [availableIcons, setAvailableIcons] = useState<IconFile[]>([]);
  const [availableColumns, setAvailableColumns] = useState<ColumnOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editColumnName, setEditColumnName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const MAX_ACTIVE = 8;

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get("/lock-properties");
      setProperties(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching lock properties:", err);
      setError(t("Failed to load lock properties"));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableIcons = async () => {
    try {
      const response = await adminApi.get("/lock-properties/icons");
      setAvailableIcons(response.data);
    } catch (err) {
      console.error("Error fetching available icons:", err);
    }
  };

  const fetchAvailableColumns = async () => {
    try {
      const response = await adminApi.get("/system-options/available-columns");
      setAvailableColumns(response.data);
    } catch (err) {
      console.error("Error fetching available columns:", err);
    }
  };

  useEffect(() => {
    fetchProperties();
    fetchAvailableIcons();
    fetchAvailableColumns();
  }, []);

  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('icon', file);

    try {
      setUploadingIcon(true);
      const response = await adminApi.post(
        "/lock-properties/icons",
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      await fetchAvailableIcons();
      setEditIcon(response.data.path);
      //alert('Icon uploaded successfully!');
    } catch (error) {
      console.error('Error uploading icon:', error);
      alert('Failed to upload icon');
    } finally {
      setUploadingIcon(false);
      event.target.value = '';
    }
  };

  const handleIconDelete = async (iconName: string) => {
    if (!window.confirm(`${t("Are you sure you want to delete the icon")} "${iconName}"?`)) {
      return;
    }

    try {
      await adminApi.delete(`/lock-properties/icons/${iconName}`);
      await fetchAvailableIcons();

      if (editIcon === `/img/specialIcon/${iconName}`) {
        setEditIcon("");
      }

      //alert(`Icon "${iconName}" deleted successfully`);
    } catch (err: any) {
      console.error("Error deleting icon:", err);
      if (err.response?.status === 400 && err.response?.data?.usedBy) {
        alert(`${t("Cannot delete icon. It is still used by:")} ${err.response.data.usedBy.join(', ')}`);
      } else {
        alert(t("Failed to delete icon"));
      }
    }
  };

  const getActiveCount = (props: LockProperty[]) => {
    return props.filter(p => p.is_active).length;
  };

  const handleToggleActive = async (id: number) => {
    const property = properties.find(p => p.id === id);
    if (!property) return;

    const newActiveState = !property.is_active;

    // Jeśli próbujemy aktywować i już jest 8 aktywnych
    if (newActiveState && getActiveCount(properties) >= MAX_ACTIVE) {
      alert(`${t("Maximum")} ${MAX_ACTIVE} ${t("active properties allowed!")}`);
      return;
    }

    const updatedProperties = properties.map(p =>
      p.id === id ? { ...p, is_active: newActiveState } : p
    );

    setProperties(updatedProperties);

    try {
      await adminApi.put(`/lock-properties/${id}`, {
        id,
        name: property.name,
        column_name: property.column_name,
        description: property.description,
        icon_path: property.icon_path,
        display_order: property.display_order,
        is_active: newActiveState
      });
    } catch (error) {
      console.error("Error toggling property:", error);
      // Revert on error
      setProperties(properties);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newProperties = [...properties];
    [newProperties[index - 1], newProperties[index]] = [newProperties[index], newProperties[index - 1]];

    newProperties.forEach((prop, idx) => {
      prop.display_order = idx;
    });

    setProperties(newProperties);

    // Save each updated property
    try {
      await Promise.all(newProperties.map(prop =>
        adminApi.put(`/lock-properties/${prop.id}`, prop)
      ));
    } catch (error) {
      console.error("Error moving property:", error);
      setProperties(properties); // Revert on error
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === properties.length - 1) return;
    const newProperties = [...properties];
    [newProperties[index], newProperties[index + 1]] = [newProperties[index + 1], newProperties[index]];

    newProperties.forEach((prop, idx) => {
      prop.display_order = idx;
    });

    setProperties(newProperties);

    // Save each updated property
    try {
      await Promise.all(newProperties.map(prop =>
        adminApi.put(`/lock-properties/${prop.id}`, prop)
      ));
    } catch (error) {
      console.error("Error moving property:", error);
      setProperties(properties); // Revert on error
    }
  };

  const handleEdit = (property: LockProperty) => {
    setEditingId(property.id);
    setEditName(property.name);
    setEditColumnName(property.column_name || "");
    setEditDescription(property.description || "");
    setEditIcon(property.icon_path || "");
  };

  const handleSaveEdit = async () => {
    if (editingId === null) return;

    try {
      await adminApi.put(`/lock-properties/${editingId}`, {
        id: editingId,
        name: editName,
        column_name: editColumnName,
        description: editDescription,
        icon_path: editIcon,
        display_order: properties.find(p => p.id === editingId)?.display_order || 0,
        is_active: properties.find(p => p.id === editingId)?.is_active || false
      });

      await fetchProperties();
      setEditingId(null);
      setEditName("");
      setEditColumnName("");
      setEditDescription("");
      setEditIcon("");
    } catch (error) {
      console.error("Error saving property:", error);
      alert(t("Failed to save property"));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditColumnName("");
    setEditDescription("");
    setEditIcon("");
  };

  const handleAdd = async () => {
    try {
      const response = await adminApi.post("/lock-properties", {
        name: t("New Property"),
        icon_path: ""
      });

      if (response.data.success) {
        await fetchProperties();
      }
    } catch (err) {
      console.error("Error adding lock property:", err);
      setError(t("Failed to add lock property"));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t("Are you sure you want to delete this property?"))) return;

    try {
      await adminApi.delete(`/lock-properties/${id}`);
      await fetchProperties();
    } catch (err) {
      console.error("Error deleting lock property:", err);
      setError(t("Failed to delete lock property"));
    }
  };

  if (loading) return <div className="loading">{t("Loading...")}</div>;
  if (error) return <div className="error">{error}</div>;

  const activeCount = getActiveCount(properties);

  return (
    <div className="lock-properties">
      <div className="properties-header">
        <div>
          <h2>{t("Lock Properties Management")}</h2>
          <p className="properties-subtitle">
            {t("Active properties:")} {activeCount} / {MAX_ACTIVE} {t("(displayed on page 2)")}
          </p>
        </div>
        <button className="btn-add" onClick={handleAdd}>
          <Plus size={16} /> {t("Add Property")}
        </button>
      </div>

      {properties.length === 0 ? (
        <div className="empty-state">
          <p>{t("No lock properties found. Click \"Add Property\" to create one.")}</p>
        </div>
      ) : (
        <ul>
          {properties.map((property, index) => (
            <li
              key={property.id}
              className={`property-item ${property.is_active ? 'active' : 'inactive'}`}
            >
              {editingId === property.id ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>{t("Name:")}</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder={t("Property name")}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t("Property Code:")}</label>
                    <select
                      value={editColumnName}
                      onChange={(e) => setEditColumnName(e.target.value)}
                      className="column-dropdown"
                    >
                      <option value="">{t("-- Select Property Code --")}</option>
                      {availableColumns.map((col) => (
                        <option key={col.name} value={col.name}>
                          {col.displayName} ({col.name})
                        </option>
                      ))}
                    </select>
                    <small>{t("Unique identifier for this property")}</small>
                  </div>

                  <div className="form-group">
                    <label>{t("Description:")}</label>
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder={t("Description shown to users")}
                      rows={2}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t("Icon:")}</label>
                    <div className="icon-selector">
                      <select
                        value={editIcon}
                        onChange={(e) => setEditIcon(e.target.value)}
                        className="icon-dropdown"
                      >
                        <option value="">{t("-- Select Icon --")}</option>
                        {availableIcons.map((icon) => (
                          <option key={icon.path} value={icon.path}>
                            {icon.name}
                          </option>
                        ))}
                      </select>

                      <label className="upload-btn">
                        <Upload size={16} />
                        {uploadingIcon ? t("Uploading...") : t("Upload New")}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleIconUpload}
                          style={{ display: 'none' }}
                          disabled={uploadingIcon}
                        />
                      </label>
                    </div>

                    {/* Icon Management Section */}
                    {availableIcons.length > 0 && (
                      <div className="icon-management">
                        <h4>{t("Available Icons:")}</h4>
                        <div className="icon-grid">
                          {availableIcons.map((icon) => (
                            <div key={icon.path} className="icon-item-small">
                              <img
                                src={`${backendUrl}${icon.path}`}
                                alt={icon.name}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              <span className="icon-name">{icon.name}</span>
                              <button
                                className="delete-icon-btn"
                                onClick={() => handleIconDelete(icon.name)}
                                title={`${t("Delete")} ${icon.name}`}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {editIcon && (
                      <div className="icon-preview">
                        <img
                          src={`${backendUrl}${editIcon}`}
                          alt={t("Icon preview")}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button className="btn-save" onClick={handleSaveEdit}>
                      <Save size={16} /> {t("Save")}
                    </button>
                    <button className="btn-cancel" onClick={handleCancelEdit}>
                      <X size={16} /> {t("Cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="property-info">
                    <div className="property-details">
                      <span className="property-name">{property.name}</span>
                      {property.column_name && (
                        <span className="property-column">{t("Code:")} {property.column_name}</span>
                      )}
                      {property.description && (
                        <span className="property-description">{property.description}</span>
                      )}
                      {property.icon_path && (
                        <div className="property-icon-display">
                          <img
                            src={`${backendUrl}${property.icon_path}`}
                            alt={property.name}
                            style={{ width: '28px', height: '28px' }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          <span className="property-icon-path">{property.icon_path}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="property-actions">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={!!property.is_active}
                        onChange={() => handleToggleActive(property.id)}
                      />
                      <span className="slider"></span>
                      <span className="toggle-label">
                        {property.is_active ? t("Active") : t("Inactive")}
                      </span>
                    </label>
                    <button className="edit-btn" onClick={() => handleEdit(property)}>
                      <Edit size={16} />
                    </button>
                    <button
                      className="arrow-btn"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      className="arrow-btn"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === properties.length - 1}
                    >
                      <ArrowDown size={16} />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(property.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default LockProperties;
